<template>
  <message-dialog
    v-model="pickerOpen"
    :title="pickerTitle"
    with-close
  >
    <q-item-label class="q-py-sm">
      {{ pickerPrompt }}
    </q-item-label>

    <q-input
      v-if="pickerInvoices.length > 5"
      v-model="pickerFilter"
      dense
      outlined
      clearable
      debounce="200"
      placeholder="Filter invoices..."
      class="q-mb-sm"
    >
      <template #prepend>
        <q-icon name="mdi-magnify" />
      </template>
    </q-input>

    <q-item-label
      v-if="pickerFilter && filteredPickerInvoices.length === 0"
      caption
      class="q-py-md"
    >
      No invoices match "{{ pickerFilter }}".
    </q-item-label>

    <q-list
      separator
      class="q-mt-sm"
      style="max-height: 50vh; overflow-y: auto;"
    >
      <q-item
        v-for="invoice in filteredPickerInvoices"
        :key="'action-' + invoice.id"
        v-close-popup
        clickable
        @click="run(invoice.id)"
      >
        <q-item-section>
          <q-item-label>{{ invoice.invoice_number }}</q-item-label>
          <q-item-label
            v-if="clientLabel(invoice)"
            caption
            lines="1"
          >
            {{ clientLabel(invoice) }}
          </q-item-label>
        </q-item-section>
        <q-item-section
          side
          class="gt-xs"
        >
          <InvoiceStatus :invoice="invoice" />
        </q-item-section>
        <q-item-section
          side
          class="text-weight-bold"
        >
          {{ friendlyNumber(invoice.total, true) }}
        </q-item-section>
      </q-item>
    </q-list>
  </message-dialog>

  <TakePaymentDialog
    v-if="activeInvoice && activeAction === 'pay'"
    ref="paymentDialog"
    :invoice="activeInvoice"
    @saved="onDone"
  />

  <PaymentReminderDialog
    v-if="activeInvoice && activeInvoice.client && activeAction === 'remind'"
    :id="activeInvoice.id"
    ref="reminderDialog"
    :client="activeInvoice.client"
    @sent="onDone"
  />
</template>

<script>
import InvoiceStatus from 'components/app/invoicing/partials/Status'
import TakePaymentDialog from 'components/app/invoicing/partials/TakePaymentDialog.vue'
import PaymentReminderDialog from 'components/app/invoicing/partials/PaymentReminderDialog'

/*
  Shared invoice actions for billing — take a payment or send a payment reminder.
  Driven by either an appointment (appointmentId) or a client (clientId).
  Call takePayment(invoiceId?) or sendReminder(invoiceId?):
  - with an id: act on that invoice directly.
  - without: look up the payable (OPEN/PARTIAL) invoices — the appointment's
    billing timeline, or the client's invoices — and, if there are several, let
    the user pick one first.
  The full invoice is fetched on demand because the summary lacks the
  payment/total/client fields the dialogs need. Emits "updated" after a payment.
*/
export default {
  components: {
    InvoiceStatus,
    TakePaymentDialog,
    PaymentReminderDialog
  },
  props: {
    appointmentId: {
      type: Number,
      default: null
    },
    clientId: {
      type: Number,
      default: null
    }
  },
  emits: ['updated'],
  data () {
    return {
      pickerOpen: false,
      pickerInvoices: [],
      pickerFilter: '',
      activeInvoice: null,
      // pendingAction is the in-flight intent; activeAction is committed
      // alongside activeInvoice so the opened dialog always matches its invoice.
      pendingAction: null,
      activeAction: null,
      busy: false
    }
  },
  computed: {
    pickerTitle () {
      return this.pendingAction === 'remind' ? 'Send Payment Reminder' : 'Take Payment'
    },
    pickerPrompt () {
      return this.pendingAction === 'remind'
        ? 'Which invoice would you like to send a reminder for?'
        : 'Which invoice would you like to take a payment for?'
    },
    filteredPickerInvoices () {
      const term = (this.pickerFilter || '').toLowerCase().trim()
      if (!term) {
        return this.pickerInvoices
      }
      return this.pickerInvoices.filter(invoice =>
        (invoice.invoice_number || '').toLowerCase().includes(term) ||
        this.clientLabel(invoice).toLowerCase().includes(term)
      )
    }
  },
  methods: {
    takePayment (invoiceId = null) {
      this.launch('pay', invoiceId)
    },
    sendReminder (invoiceId = null) {
      this.launch('remind', invoiceId)
    },
    async launch (action, invoiceId) {
      // Guard against a second click landing mid-fetch and acting on the wrong invoice.
      if (this.busy) {
        return
      }
      this.pendingAction = action
      if (invoiceId) {
        this.run(invoiceId)
        return
      }

      this.busy = true
      this.$loading.show()
      const payable = await this.fetchPayableInvoices()
      this.$loading.hide()
      this.busy = false
      if (payable === null) {
        return
      }

      if (payable.length === 0) {
        this.$q.notify({
          color: 'orange-7',
          textColor: 'black',
          message: action === 'remind'
            ? 'No outstanding invoices to send a reminder for.'
            : 'No invoices are awaiting payment for this appointment.'
        })
        return
      }

      if (payable.length === 1) {
        this.run(payable[0].id)
        return
      }

      this.pickerInvoices = payable
      this.pickerFilter = ''
      this.pickerOpen = true
    },
    // Source the OPEN/PARTIAL invoices to act on. A clientId pulls from the
    // client's invoices; otherwise we fall back to the appointment's billing
    // timeline. Returns null on a failed fetch so the caller can bail.
    async fetchPayableInvoices () {
      if (this.clientId) {
        const { status, payload } = await this.$get('v1/invoice/paginate', {
          clients: [this.clientId],
          status: ['OPEN', 'PARTIAL'],
          archived: 0,
          page: 1,
          rows: 100,
          sortBy: 'invoice_date',
          sortDirection: 'desc'
        })
        return status === 'success' ? (payload.invoices || []) : null
      }

      const { status, payload } = await this.$get('v1/appointments/timeline', {
        id: this.appointmentId
      })
      if (status !== 'success') {
        return null
      }
      return (payload.timeline || []).filter(entry =>
        entry.timeline_type === 'invoice' && (entry.status === 'OPEN' || entry.status === 'PARTIAL')
      )
    },
    // The appointment timeline gives client as a string; paginate gives a client
    // object — normalise to a display string for the picker.
    clientLabel (invoice) {
      if (!invoice.client) {
        return ''
      }
      return typeof invoice.client === 'string'
        ? invoice.client
        : (invoice.client.display_name || '')
    },
    async run (invoiceId) {
      if (this.busy) {
        return
      }
      this.busy = true
      this.$loading.show()
      const { status, payload } = await this.$get('v1/invoice', { invoice_id: invoiceId })
      this.$loading.hide()
      this.busy = false
      if (status !== 'success') {
        return
      }

      const invoice = payload.invoice
      if (this.pendingAction === 'remind' && !invoice.client) {
        this.$q.notify({
          color: 'orange-7',
          textColor: 'black',
          message: 'This invoice has no client to send a reminder to.'
        })
        return
      }

      this.activeInvoice = invoice
      this.activeAction = this.pendingAction
      this.$nextTick(() => {
        if (this.activeAction === 'pay') {
          this.$refs.paymentDialog.open()
        }
        else {
          this.$refs.reminderDialog.open()
        }
      })
    },
    onDone () {
      this.activeInvoice = null
      this.$emit('updated')
    }
  }
}
</script>
